import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Clock, ShieldCheck, CurrencyEur, Lightning, Drop, Flame, Virus, ShieldWarning, Handshake } from '@phosphor-icons/react';
import ProductCard from '@/components/ProductCard';
import SectionHeader from '@/components/SectionHeader';
import { API } from '@/lib/utils';
import { useSEO } from '@/lib/useSEO';

const ICONS = { Lightning, Drop, Flame, Virus, ShieldWarning, ShieldCheck };

export default function Home() {
  const [bestBuys, setBestBuys] = useState([]);
  const [types, setTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);

  useSEO({
    title: null,
    description: 'Bouw jouw persoonlijke noodpakket voor stroomuitval, overstroming, brand of andere crisis. Vergelijk 50+ producten en bestel direct bij Bol.com, Amazon of Coolblue.',
    path: '/',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Noodpakket.nl',
      url: 'https://noodpakket.nl',
      inLanguage: 'nl-NL',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://noodpakket.nl/categorieen?query={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
  });

  useEffect(() => {
    axios.get(`${API}/products?best_buy=true&limit=8`).then((r) => setBestBuys(Array.isArray(r.data) ? r.data : [])).catch(console.error);
    axios.get(`${API}/emergency-types`).then((r) => setTypes(Array.isArray(r.data) ? r.data : [])).catch(console.error);
    axios.get(`${API}/categories`).then((r) => setCategories(Array.isArray(r.data) ? r.data : [])).catch(console.error);
    axios.get(`${API}/blog`).then((r) => setPosts(Array.isArray(r.data) ? r.data.slice(0, 3) : [])).catch(console.error);
  }, []);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 grid-lines pointer-events-none" />
        <div className="container relative pt-14 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="overline text-muted-foreground mb-6 flex items-center gap-3">
              <span className="w-10 h-0.5 bg-accent" />
              Onafhankelijk Nederlands platform
            </div>
            <h1 className="font-display font-black tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-7xl">
              Wees klaar <br />
              <span className="relative inline-block">
                <span className="relative z-10">vóór</span>
                <span className="absolute inset-x-0 bottom-2 h-3 bg-accent/80 -z-0" />
              </span>{' '}
              de storm.
            </h1>
            <p className="mt-7 text-lg sm:text-xl text-muted-foreground max-w-xl leading-relaxed">
              Bouw in 3 minuten een noodpakket op maat voor jouw huishouden.
              Vergelijk producten, lees reviews en bestel bij jouw favoriete shop.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link
                to="/wizard"
                data-testid="hero-start-wizard"
                className="inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent-hover text-accent-foreground px-7 py-4 rounded-sm font-bold text-base transition-colors"
              >
                Start de pakket-wizard <ArrowRight size={20} weight="bold" />
              </Link>
              <Link
                to="/beste-keuzes"
                data-testid="hero-best-buys"
                className="inline-flex items-center justify-center gap-2 bg-secondary hover:bg-secondary-hover text-secondary-foreground px-7 py-4 rounded-sm font-bold text-base transition-colors"
              >
                Bekijk beste keuzes
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><Clock size={18} weight="duotone" className="text-primary" /> 72 uur zelfredzaam</div>
              <div className="flex items-center gap-2"><ShieldCheck size={18} weight="duotone" className="text-primary" /> Rijksoverheid-advies</div>
              <div className="flex items-center gap-2"><CurrencyEur size={18} weight="duotone" className="text-primary" /> Geen verborgen kosten</div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-sm overflow-hidden border border-border bg-card aspect-[4/5]">
              <img
                src="https://images.pexels.com/photos/11182954/pexels-photo-11182954.jpeg"
                alt="Gezin dat voorbereid is op crisis"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/10 to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-6 text-background">
                <div className="overline text-background/80 mb-2">Feit</div>
                <p className="font-display font-bold text-lg leading-tight">
                  De Nederlandse overheid adviseert iedereen om minimaal 72 uur zelfredzaam te kunnen zijn.
                </p>
              </div>
            </div>
            <div className="absolute -left-6 top-6 bg-primary text-primary-foreground px-4 py-3 rounded-sm shadow-lg hidden md:block">
              <div className="text-[10px] overline text-background/70">Gratis tool</div>
              <div className="font-display font-black">Bouw je pakket</div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="bg-foreground text-background">
        <div className="container py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { n: '72u', l: 'Minimum voorbereiding' },
            { n: '6', l: 'Noodsituaties gedekt' },
            { n: '50+', l: 'Producten vergeleken' },
            { n: '100%', l: 'Onafhankelijk advies' },
          ].map((s, i) => (
            <div key={i} className="flex flex-col" data-testid={`stat-${i}`}>
              <div className="font-display font-black text-3xl lg:text-4xl text-accent">{s.n}</div>
              <div className="text-xs overline text-background/60 mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* EMERGENCY TYPES */}
      <section className="container py-20 lg:py-28">
        <SectionHeader
          eyebrow="Noodsituaties"
          title="Voor welke crisis wil je voorbereid zijn?"
          description="Elke ramp vraagt om ander materiaal. Kies een scenario en zie precies wat je nodig hebt."
          accent
        />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {(types || []).map((t, i) => {
            const Icon = ICONS[t.icon] || ShieldCheck;
            const isFeatured = i === 0;
            return (
              <Link
                key={t.slug}
                to={`/noodsituaties/${t.slug}`}
                data-testid={`emergency-type-${t.slug}`}
                className={`group relative border border-border rounded-sm overflow-hidden bg-card hover:-translate-y-1 transition-all duration-200 hover:shadow-lg ${isFeatured ? 'md:col-span-2 md:row-span-2' : ''}`}
              >
                <div className={`relative ${isFeatured ? 'aspect-[16/10]' : 'aspect-[4/3]'}`}>
                  <img src={t.image} alt={t.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${t.color}33 0%, ${t.color}aa 55%, ${t.color}ee 100%)` }} />
                  <div className="absolute top-4 left-4 w-10 h-10 bg-background/90 text-foreground rounded-sm flex items-center justify-center backdrop-blur">
                    <Icon weight="duotone" size={22} />
                  </div>
                </div>
                <div className="absolute bottom-0 inset-x-0 p-5 text-background">
                  <div className="overline text-background/80 mb-2">{t.short}</div>
                  <h3 className={`font-display font-black ${isFeatured ? 'text-3xl lg:text-4xl' : 'text-xl'} leading-tight`}>
                    {t.name}
                  </h3>
                  <div className="mt-3 text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Bekijk producten <ArrowRight size={16} weight="bold" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* BEST BUYS */}
      <section className="bg-muted/50 border-y border-border">
        <div className="container py-20 lg:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <SectionHeader
              eyebrow="Beste keuzes 2026"
              title="Getest en onafhankelijk beoordeeld"
              description="Onze redactie heeft honderden reviews en specs vergeleken. Dit zijn de producten die écht het verschil maken."
              accent
            />
            <Link
              to="/beste-keuzes"
              data-testid="home-view-all-best"
              className="text-primary font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all whitespace-nowrap"
            >
              Alle beste keuzes <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {(bestBuys || []).slice(0, 8).map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="container py-20 lg:py-28">
        <SectionHeader
          eyebrow="Categorieën"
          title="Blader op productsoort"
          description="Weet je wat je zoekt? Ga direct naar de categorie."
          accent
        />
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
          {(categories || []).map((c) => (
            <Link
              key={c.slug}
              to={`/categorieen/${c.slug}`}
              data-testid={`category-tile-${c.slug}`}
              className="group border border-border hover:border-primary hover:bg-primary hover:text-primary-foreground bg-card p-6 rounded-sm transition-colors"
            >
              <div className="overline text-muted-foreground group-hover:text-primary-foreground/70 transition-colors mb-2">Categorie</div>
              <div className="font-display font-black text-lg leading-tight">{c.name}</div>
              <div className="mt-8 text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Bekijken <ArrowRight size={14} weight="bold" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* LATEST ARTICLES */}
      {posts.length > 0 && (
        <section className="bg-muted/40 border-y border-border">
          <div className="container py-20 lg:py-24">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
              <SectionHeader
                eyebrow="Gids & artikelen"
                title="Lees, leer, bereid je voor"
                description="Diepgaande Nederlandse gidsen over noodpakketten, stroomuitval, EHBO en meer."
                accent
              />
              <Link
                to="/gids"
                data-testid="home-view-all-blog"
                className="text-primary font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all whitespace-nowrap"
              >
                Alle artikelen <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {(posts || []).map((post, i) => (
                <Link
                  key={post.slug}
                  to={`/gids/${post.slug}`}
                  data-testid={`home-blog-${post.slug}`}
                  className="group bg-card border border-border hover:border-primary rounded-sm overflow-hidden hover:-translate-y-1 transition-all duration-200 hover:shadow-lg flex flex-col animate-fade-up"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <div className="aspect-[16/10] bg-muted overflow-hidden">
                    <img src={post.hero_image} alt={post.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="overline text-muted-foreground mb-3">{post.category} · {post.reading_time_min} min</div>
                    <h3 className="font-display font-bold text-lg leading-tight mb-3 group-hover:text-primary transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 flex-1">{post.excerpt}</p>
                    <div className="mt-5 text-sm font-semibold text-primary inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                      Lees verder <ArrowRight size={14} weight="bold" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA BLOCK */}
      <section className="container py-24">
        <div className="relative bg-primary text-primary-foreground rounded-sm overflow-hidden">
          <div className="absolute inset-0 opacity-20 grid-lines" />
          <div className="relative grid md:grid-cols-5 gap-8 p-10 lg:p-16 items-center">
            <div className="md:col-span-3">
              <div className="overline text-primary-foreground/70 mb-4 flex items-center gap-2">
                <span className="w-10 h-0.5 bg-accent" /> In 3 minuten klaar
              </div>
              <h2 className="font-display font-black text-3xl lg:text-5xl leading-tight">
                Wil jij het écht goed doen?
              </h2>
              <p className="mt-5 text-primary-foreground/80 text-lg max-w-xl leading-relaxed">
                Onze wizard stelt een persoonlijk noodpakket samen op basis van je huishouden. Met directe links naar jouw favoriete shop.
              </p>
            </div>
            <div className="md:col-span-2 flex md:justify-end">
              <Link
                to="/wizard"
                data-testid="home-cta-wizard"
                className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-accent-foreground px-7 py-4 rounded-sm font-bold text-base transition-colors"
              >
                Start wizard <ArrowRight size={20} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
